<template>
    <div
        v-show="hasFilters"
        class="l-ss-c-filters-container"
        :class="{
            'l-ss-c-filters-container-all-filters': isShowHiddenFilters,
        }"
        ref="filtersContainer">
        <div
            class="l-ss-c-filters-top-container"
            ref="filtersTopContainer">
            <div
                v-if="showTopFilters"
                class="l-ss-c-filters-top-list"
                :class="{ 'l-ss-c-filters-top-list-hidden': calculatingFiltersWidth && !filterPositionLeft }"
                ref="topList">
                <section-image
                    v-if="base64Image && isShowImageSearchInFilters"
                    :key="base64ImageKey"
                    :base-image="base64Image"
                    :has-initial-search="false"
                    :store="store"
                    :custom-canvas-width="isShowImageSearchInFilters ? '240' : ''"
                    class="short-height"
                    @replace-image="$emit('replace-image')"
                    @search="$emit('search-image', $event)"/>

                <filter-container
                    v-for="filter in topFilters"
                    :key="filter.id"
                    :class="{ [`l-ss-c-filters-filter-${filter.type}`]: true, [`l-ss-c-filters-top-item`]: true }"
                    :filter="filter"
                    :has-results="hasResults"
                    :is-short="true"
                    :value="resolveValue(filter)"
                    :all-categories-list="allCategoriesList"
                    :symbol="getSymbol(filter.id)"
                    @hook:mounted="onMountedFilter"
                    @change="handleChangeFilter"
                    @change-size="onChangeFiltersSize"
                    @filter-click="params => $emit('filter-click', params)"/>

                <signature v-if="isShowSignature && filterPositionLeft"/>

                <div
                    v-if="isAllowAllFiltersBtn && !filterPositionLeft"
                    class="l-ss-c-filters-all"
                    ref="allBtn"
                    @click="showAllFilters">
                    {{ $t('filters.titleAll') }}
                </div>
            </div>
            <div
                v-if="$screen.isMobile"
                class="l-ss-c-filters-all-filters"
                :class="{ 'l-ss-c-filters-all-filters-active': !!activeFiltersCount }"
                @click="$emit('open-filters')">
                <i v-html="iconFunnel"></i>
                <span class="l-ss-c-filters-all-filters-label">{{ $t('filters.title') }}</span>
                <span
                    v-if="!!activeFiltersCount"
                    class="l-ss-c-filters-all-filters-count l-ss-c-count-tag">{{ activeFiltersCount }}</span>
            </div>
            <sort
                v-if="showSort"
                class="l-ss-c-filters-sort"
                ref="sortContainer"
                :sort-by="sortBy"
                :sort-dest="sortDest"
                @sort-change="params => $emit('sort-change', params)"
                @result-sort-click="params => $emit('result-sort-click', params)"/>
        </div>
    </div>
</template>

<script>
    import FilterMixin from './FilterMixin';
    import Sort from '../../../core/components/sort';
    import Signature from '@/core/components/signature';
    import iconFunnel from '@/core/assets/images/icons/funnel.svg';
    import useStickyLeftFilters from '@/core/components/filters/useStickyLeftFilters';
    import SectionImage from '@/core/components/section-image';

    export default {
        components: {
            Sort,
            Signature,
            SectionImage,
        },
        mixins: [
            FilterMixin,
        ],
        inject: ['scrollToTop'],
        props: {
            currency: {
                type: String,
                default: '',
            },
            hasSelected: {
                type: Boolean,
                default: false,
            },
            sortBy: {
                type: String,
                default: '',
            },
            sortDest: {
                type: String,
                default: '',
            },
            showSorting: {
                type: Boolean,
                default: false,
            },
            filterPositionLeft: {
                type: Boolean,
                default: false,
            },
            searchType: {
                type: String,
                default: 'search-popup',
            },
            isShowSignature: {
                type: Boolean,
                default: false,
            },
            allCategoriesList: {
                type: Array,
            },
            base64ImageKey: {
                type: Boolean,
                default: false,
            },
            base64Image: {
                type: String,
                default: '',
            },
            store: {
                type: Object,
            },
        },
        data() {
            return {
                mountedFilters: 0,
                countFiltersForShow: 0,
                calculatingFiltersWidth: true,
                isAllowAllFiltersBtn: true,
                isShowHiddenFilters: false,
                root: null,
                changeFilterSizeTrigger: 0,
                onChangeFiltersSize: () => {
                },
                pauseStickyFilters: null,
                resumeStickyFilters: null,
                waitingForNewFilters: false,
            };
        },
        computed: {
            iconFunnel() {
                return iconFunnel;
            },
            showTopFilters() {
                return this.$screen.isMobile === false;
            },
            showSort() {
                return this.showSorting && this.$configuration.isSortVisible && !this.filterPositionLeft;
            },
            hasFilters() {
                return !!this.filters.length;
            },
            activeFiltersCount() {
                let count = 0;

                this.values.forEach(valueItem => {
                    if (valueItem.options) {
                        count += valueItem.options.length;
                    } else {
                        count += 1;
                    }
                });

                return count;
            },
            checkFilters() {
                const filters = [];

                this.filters.forEach((filter) => {
                    if (!this.$configuration.hiddenFilters.includes(filter.name)) {
                        if (this.$configuration.categoriesAsTree && filter.name === 'category') {
                            filters.push({
                                ...filter,
                                type: 'tree-enum',
                            });
                        } else {
                            filters.push(filter);
                        }
                    }
                });

                return filters;
            },
            topFilters() {
                return this.checkFilters.slice(0, this.countFiltersForShow);
            },
            isShowImageSearchInFilters() {
                return this.$configuration.isShowImageSearchInFilters && this.$configuration.layout.filterPosition === 'left';
            },
        },
        watch: {
            filters(newFilters, oldFilters) {
                let doNeedRecalculateFiltersWidth = false;

                if (newFilters.length !== oldFilters.length) {
                    doNeedRecalculateFiltersWidth = true;
                } else {
                    for (let i = 0; i < newFilters.length; ++i) {
                        const newFilter = newFilters[i];
                        const oldFilter = oldFilters[i];

                        if (newFilter.id !== oldFilter.id) {
                            doNeedRecalculateFiltersWidth = true;
                            break;
                        }
                    }
                }

                if (doNeedRecalculateFiltersWidth) {
                    this.initFiltersWidthCalculation();
                }

                if (this.waitingForNewFilters) {
                    /*
                     * Const root = document
                     *     .querySelector('.l-ss-c-host-node-is-search-popup').shadowRoot
                     *     .querySelector('.l-ss-c-search-top-section-wrapper');
                     * root.scrollIntoView();
                     */
                    this.scrollToTop({
                        onScrollEnd: () => {
                            this.resumeStickyFilters();
                        },
                    });

                    this.waitingForNewFilters = false;
                }
            },
        },
        created() {
            this.initFiltersWidthCalculation();
        },
        mounted() {
            if (this.filterPositionLeft) {
                let root = this.$parent.$el.closest('.l-ss-c-root');
                let mainContent;

                if (root.classList.contains('l-ss-c-complex-popup')) {
                    root = root.querySelector('.l-ss-c-complex-search-results-wrapper');
                    mainContent = root.querySelector('.l-ss-c-search-popup-content');
                } else if (root.classList.contains('l-ss-c-complex')) {
                    mainContent = root;
                    root = document;
                } else {
                    root = this.$parent.$el.closest('.l-ss-c-search-popup-wrapper-mask');
                    mainContent = root.querySelector('.l-ss-c-search-popup-content-wrapper');
                }

                const {
                    onChangeFiltersSize,
                    pause,
                    resume,
                } = useStickyLeftFilters(root, this.$refs.filtersTopContainer, mainContent);

                this.onChangeFiltersSize = onChangeFiltersSize;
                this.pauseStickyFilters = pause;
                this.resumeStickyFilters = resume;
            }
        },
        methods: {
            handleChangeFilter(data) {
                if (this.filterPositionLeft) {
                    this.waitingForNewFilters = true;
                    this.pauseStickyFilters();
                }

                this.$emit('filters-change', data);

            // This.onFilterChange(data);
            },
            initFiltersWidthCalculation() {
                this.countFiltersForShow = 0;
                this.$nextTick(() => {
                    this.countFiltersForShow = this.checkFilters.length;
                    this.calculatingFiltersWidth = true;
                    this.mountedFilters = 0;
                    this.isAllowAllFiltersBtn = true;
                    this.isShowHiddenFilters = false;
                });
            },
            onMountedFilter() {
                if (!this.calculatingFiltersWidth || this.filterPositionLeft) {
                    return;
                }

                ++this.mountedFilters;

                if (this.mountedFilters === this.topFilters.length) {
                    this.calcFiltersWidth();
                }
            },
            calcFiltersWidth() {
                // Ширина блока с фильтрами
                const filtersWidth = this.$refs.topList.clientWidth;
                /*
                 * Ширина блока с фильтрами и кнопкой сортировки если она есть
                 */
                const containerWidth = this.showSort ?
                    this.$refs.filtersTopContainer.clientWidth - this.$refs.sortContainer.$el.clientWidth :
                    this.$refs.filtersTopContainer.clientWidth;
                const allFiltersBtnWidth = this.$refs.allBtn.clientWidth;

                // Если ширина блока с фильтрами меньше ширина контейнера то отображаем все фильтры
                if (filtersWidth <= containerWidth) {
                    this.calculatingFiltersWidth = false;
                    this.isAllowAllFiltersBtn = false;
                    return;
                }

                const filters = this.$refs.topList.querySelectorAll('.l-ss-c-filter-container');

                let lastWidth = 0;
                let currentWidth = 0;
                let i = 0;

                const gap = 32;

                // Считаем ширину каждого фильтра на каждой итерации цикла
                for (let filterNode of filters) {
                    if (i > 0) {
                        currentWidth += gap;
                    }

                    currentWidth += filterNode.clientWidth;

                    /*
                     * Если ширина фильтров на текущей итерации больше ширина контейнера, то отображаем кол-во фильтров
                     * равное кол-ву фильтров на прошлой итерации
                     */
                    if (currentWidth > containerWidth) {
                        currentWidth = lastWidth;
                        --i;

                        break;
                    }

                    lastWidth = currentWidth;

                    ++i;
                }

                if (i < this.checkFilters.length) {
                    const widthWithAllFiltersBtn = currentWidth - allFiltersBtnWidth;

                    if (widthWithAllFiltersBtn > containerWidth) {
                        --i;
                    }
                } else {
                    this.isAllowAllFiltersBtn = false;
                }

                this.countFiltersForShow = i;
                this.calculatingFiltersWidth = false;
            },
            showAllFilters() {
                this.countFiltersForShow = this.checkFilters.length;
                this.isAllowAllFiltersBtn = false;
                this.isShowHiddenFilters = true;
            },
        },
    };
</script>
